import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import axios from "axios";
import { jwtDecode } from 'jwt-decode';

const CompanyForm = ({ open, setOpen }) => {
 const [file, setFile] = useState(null);
 const [loading, setLoading] = useState(false);
 const [successMessage, setSuccessMessage] = useState("");
 const [errorMessage, setErrorMessage] = useState("");
 const [token, setToken] = useState("");
 const [previewUrl, setPreviewUrl] = useState('');
 const [companyData, setCompanyData] = useState({
    name: '',
    contact: '',
    location: '',
    about: '',
    logo: '', // Include the logo field in companyData
 });

 useEffect(() => {
    const usertoken = window.localStorage.getItem("token");
    if (usertoken) {
      setToken(usertoken);
      const fetchData = async () => {
        try {
          const decoded = jwtDecode(usertoken);
          const userId = decoded.userId;
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/company/get-company/${userId}`);
          console.log(response.data.data);
          setCompanyData(response.data.data);
          setPreviewUrl(response.data.data.profileUrl);
        } catch (err) {
          console.log(err);
        }
      };
      fetchData();
    }
 }, [token]);

 const handleLogoChange = (e) => {
  const file = e.target.files[0];
  setFile(file);
  // Generate a preview URL for the selected image
  const previewUrl = URL.createObjectURL(file);
  setPreviewUrl(previewUrl);
};

const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyData(prevState => ({ ...prevState, [name]: value }));
 };

const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (!token) {
        throw new Error("Token is not available.");
      }
      const decoded = jwtDecode(token, { secret: '123456' });
      const user = decoded.userId;
      const formData = new FormData();
      formData.append("name", companyData.name);
      formData.append("contact", companyData.contact);
      formData.append("location", companyData.location);
      formData.append("about", companyData.about);
      if (file) {
        formData.append("file", file, file.name);
      }
      const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/company/update-company/${user}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setLoading(false);
      if (response.status === 200) {
        setSuccessMessage("Company profile updated successfully.");
        setOpen(false);
      } else {
        setErrorMessage(response.data.message || "Failed to update company profile.");
      }
    } catch (error) {
      setLoading(false);
      if (error.response) {
        setErrorMessage(error.response.data.message || "An error occurred while updating company profile.");
      } else if (error.request) {
        setErrorMessage("No response received from the server.");
      } else {
        setErrorMessage("An error occurred while updating company profile.");
      }
    }
 };

 const closeModal = () => {
    setOpen(false);
    setSuccessMessage("");
    setErrorMessage("");
 };

 return (
    <>
      <Transition appear show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={closeModal}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <Dialog.Title
                 as="h3"
                 className="text-lg font-medium leading-6 text-gray-900"
                >
                 Edit Company Profile
                </Dialog.Title>
                <form className="mt-4" onSubmit={onSubmit}>
                 <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Company Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={companyData?.name}
                      onChange={handleInputChange}
                      className="mt-1 p-2 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                 </div>

                 <div className="mt-4">
                    <label htmlFor="location" className="block text-sm font-medium text-gray-700">Location/Address</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={companyData?.location}
                      onChange={handleInputChange}
                      className="mt-1 p-2 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                 </div>

                 <div className="mt-4">
                    <label htmlFor="contact" className="block text-sm font-medium text-gray-700">Contact</label>
                    <input
                      type="text"
                      id="contact"
                      name="contact"
                      value={companyData?.contact}
                      onChange={handleInputChange}
                      className="mt-1 p-2 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                 </div>

                 <div className="mt-4">
                    <label htmlFor="about" className="block text-sm font-medium text-gray-700">About Company</label>
                    <textarea
                      rows={4}
                      cols={6}
                      id="about"
                      name="about"
                      value={companyData?.about}
                      onChange={handleInputChange}
                      className="mt-1 p-2 block w-full border-gray-300 rounded-md  focus:outline-none  focus:ring-1 focus:ring-blue-500 text-base px-4 py-2 resize-none shadow-sm  focus:border-indigo-500"
                    ></textarea>
                 </div>
                 {previewUrl && (
                      <img src={previewUrl} alt="Logo Preview" className="mt-2 w-full h-auto" />
                    )}

                 <div className="mt-4">
                    <label htmlFor="file" className="block text-sm font-medium text-gray-700">Company Logo</label>
                    <input
                      type="file"
                      id="logo"
                      name="logo"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder-text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                 </div>

                 <div className="mt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                 </div>
                </form>

                {successMessage && (
                 <div className="mt-4 text-green-600">{successMessage}</div>
                )}

                {errorMessage && (
                 <div className="mt-4 text-red-600">{errorMessage}</div>
                )}
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
 );
};
export default CompanyForm;
